// Página de aterrizaje de SubiGol — primera impresión del producto
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Trophy, Users, BarChart3, Zap, ChevronRight,
  ArrowRight, Shield, Star, Globe, Timer,
  Menu, X, Download
} from 'lucide-react'
import client from '../api/client'
import AdCarousel from '../components/ui/AdCarousel'
import { useQuery } from '@tanstack/react-query'
import Flag from '../components/ui/Flag'
import { parseBoliviaDate } from '../api/dateUtils'
import Footer from '../components/layout/Footer'

// Animación de entrada escalonada
const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
}

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
}

// Componente de cuenta regresiva dinámica al próximo partido
function Countdown({ matches }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 })
  const [targetMatches, setTargetMatches] = useState([])
  const [targetLabel, setTargetLabel] = useState('El Mundial comienza en')

  useEffect(() => {
    if (!matches || matches.length === 0) {
      // Si no hay partidos cargados aún, cuenta regresiva por defecto al inicio del Mundial
      const worldCupDate = new Date('2026-06-11T00:00:00-05:00')
      const interval = setInterval(() => {
        const diff = worldCupDate - new Date()
        if (diff <= 0) {
          setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
        } else {
          setTimeLeft({
            days: Math.floor(diff / (1000 * 60 * 60 * 24)),
            hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
            minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
            seconds: Math.floor((diff % (1000 * 60)) / 1000),
          })
        }
      }, 1000)
      return () => clearInterval(interval)
    }

    const now = new Date()
    // Filtrar partidos programados ordenados por fecha
    const upcoming = matches
      .filter(m => m.estado === 'programado' && parseBoliviaDate(m.fecha_partido) > now)
      .sort((a, b) => parseBoliviaDate(a.fecha_partido) - parseBoliviaDate(b.fecha_partido))

    if (upcoming.length === 0) {
      // Si no hay partidos futuros programados (ej. terminó el mundial)
      setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
      setTargetMatches([])
      setTargetLabel('El Mundial ha finalizado')
      return
    }

    // Obtener la fecha del partido más cercano
    const nextMatchDateStr = upcoming[0].fecha_partido
    const targetDate = parseBoliviaDate(nextMatchDateStr)

    // Agrupar todos los partidos que jueguen exactamente en esa misma fecha/hora
    const grouped = upcoming.filter(m => m.fecha_partido === nextMatchDateStr)
    setTargetMatches(grouped)

    // Ajustar etiqueta dinámica
    if (grouped.length > 1) {
      setTargetLabel('Próximos partidos simultáneos en')
    } else {
      setTargetLabel('El próximo partido comienza en')
    }

    const updateCountdown = () => {
      const diff = targetDate - new Date()
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
      } else {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000),
        })
      }
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 1000)
    return () => clearInterval(interval)
  }, [matches])

  const units = [
    { label: 'Días', value: timeLeft.days },
    { label: 'Horas', value: timeLeft.hours },
    { label: 'Min', value: timeLeft.minutes },
    { label: 'Seg', value: timeLeft.seconds },
  ]

  return (
    <div className="w-full flex flex-col items-center gap-6">
      {/* Etiqueta dinámica */}
      <p className="w-full text-xs uppercase tracking-widest font-display flex items-center justify-center gap-2" style={{ color: '#64748B' }}>
        <Timer className="w-3.5 h-3.5" />
        {targetLabel}
      </p>

      {/* Temporizador */}
      <div className="flex gap-3 sm:gap-4">
        {units.map((unit) => (
          <div key={unit.label} className="text-center">
            <div
              className="rounded-xl px-3 py-2 sm:px-5 sm:py-3 min-w-[3.5rem] sm:min-w-[4.5rem]"
              style={{
                background: 'rgba(0, 194, 255, 0.05)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(0, 194, 255, 0.3)',
                boxShadow: '0 0 20px rgba(0, 194, 255, 0.15)',
              }}
            >
              <span
                className="countdown-digit text-2xl sm:text-4xl font-bold"
                style={{ color: '#00C2FF', textShadow: '0 0 20px rgba(0, 194, 255, 0.5)' }}
              >
                {String(unit.value).padStart(2, '0')}
              </span>
            </div>
            <p className="text-[0.625rem] sm:text-xs mt-1.5 uppercase tracking-wider" style={{ color: '#64748B' }}>
              {unit.label}
            </p>
          </div>
        ))}
      </div>

      {/* Renderizado de partidos agrupados (si existen) */}
      {targetMatches.length > 0 && (
        <div className="w-full max-w-xl mt-2 space-y-3 px-4">
          <div className="text-center text-[10px] text-text-muted font-mono tracking-wider uppercase mb-1">
            Horario: {new Date(parseBoliviaDate(targetMatches[0].fecha_partido)).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })} (BOT)
          </div>
          <div className="grid gap-3">
            {targetMatches.map((match) => (
              <div 
                key={match.id}
                className="flex items-center justify-between p-3.5 rounded-xl border border-border-subtle bg-surface-card/30 backdrop-blur-md transition-all hover:bg-surface-card/50"
              >
                {/* Equipo Local */}
                <div className="flex items-center gap-2.5 w-[42%] justify-end">
                  <span className="text-xs sm:text-sm font-semibold text-text-primary text-right truncate">
                    {match.equipo_local_nombre}
                  </span>
                  <Flag 
                    code={match.equipo_local_bandera} 
                    className="w-6.5 h-4 object-cover rounded shadow-sm shrink-0 border border-border-subtle" 
                  />
                </div>

                {/* VS */}
                <div className="flex flex-col items-center justify-center px-2">
                  <span className="text-[10px] font-bold text-cyan font-mono bg-cyan/10 px-2 py-0.5 rounded-full">VS</span>
                  <span className="text-[9px] text-text-muted mt-1 uppercase font-semibold tracking-wider font-mono">
                    {match.grupo_nombre || match.fase?.replace('_', ' ')}
                  </span>
                </div>

                {/* Equipo Visitante */}
                <div className="flex items-center gap-2.5 w-[42%] justify-start">
                  <Flag 
                    code={match.equipo_visitante_bandera} 
                    className="w-6.5 h-4 object-cover rounded shadow-sm shrink-0 border border-border-subtle" 
                  />
                  <span className="text-xs sm:text-sm font-semibold text-text-primary text-left truncate">
                    {match.equipo_visitante_nombre}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Característica del producto
function Feature({ icon: Icon, title, description }) {
  return (
    <motion.div variants={fadeUp} className="w-full">
      <div
        className="w-full rounded-xl p-6 h-full transition-all duration-200 hover:-translate-y-0.5 group"
        style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
          style={{ background: 'rgba(0, 194, 255, 0.1)' }}
        >
          <Icon className="w-5 h-5" style={{ color: '#00C2FF' }} />
        </div>
        <h3 className="font-display font-semibold text-lg mb-2" style={{ color: '#F1F5F9' }}>
          {title}
        </h3>
        <p className="text-sm leading-relaxed" style={{ color: '#94A3B8' }}>
          {description}
        </p>
      </div>
    </motion.div>
  )
}

// Paso del proceso
function Step({ number, title, description }) {
  return (
    <motion.div variants={fadeUp} className="w-full flex gap-5 items-start">
      <div className="shrink-0">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ border: '2px solid #00C2FF' }}
        >
          <span className="font-mono text-base font-bold" style={{ color: '#00C2FF' }}>
            {number}
          </span>
        </div>
      </div>
      <div className="pt-1">
        <h4 className="font-display font-semibold text-lg mb-1" style={{ color: '#F1F5F9' }}>
          {title}
        </h4>
        <p className="text-sm leading-relaxed" style={{ color: '#94A3B8' }}>
          {description}
        </p>
      </div>
    </motion.div>
  )
}

// Datos de la tabla de puntuación
const scoringRules = [
  ['Equipo en grupo correcto', '+5'],
  ['Posición exacta en grupo', '+5'],
  ['Mejor tercero correcto', '+3'],
  ['Ganador de eliminatoria', '+4'],
  ['Cuartofinalista correcto', '+6'],
  ['Semifinalista correcto', '+8'],
  ['Finalista correcto', '+10'],
  ['Campeón correcto', '+30'],
  ['Máximo goleador', '+25'],
  ['Mejor jugador', '+20'],
]

export default function Landing() {
  const [ads, setAds] = useState([])
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [isInstallable, setIsInstallable] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [showiOSModal, setShowiOSModal] = useState(false)
  const [showAndroidModal, setShowAndroidModal] = useState(false)


  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await client.get('/settings')
      return response.data?.datos || {}
    },
    staleTime: 5 * 60 * 1000,
  })

  const { data: matches } = useQuery({
    queryKey: ['matches'],
    queryFn: async () => {
      const response = await client.get('/matches')
      return response.data?.datos || []
    },
    staleTime: 60000,
  })

  useEffect(() => {
    if (settings) {
      const nombre = settings.nombre_sistema || 'SubiGol'
      const suffix = ' — Predicciones Mundial 2026'
      if (!document.title.includes(nombre)) {
        document.title = `${nombre}${suffix}`
      }

      const logoPath = settings.logo_sistema || '/favicon.svg'
      const baseImgUrl = (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.startsWith('http'))
        ? import.meta.env.VITE_API_URL.replace('/api', '')
        : (window.__APP_BASE_URL__ || '/').replace(/\/$/, '')
      const fullLogoUrl = logoPath.startsWith('/uploads') ? `${baseImgUrl}${logoPath}` : (logoPath.startsWith('/') ? `${window.__APP_BASE_URL__ || '/'}${logoPath.substring(1)}` : logoPath)

      let linkElement = document.querySelector("link[rel~='icon']")
      if (!linkElement) {
        linkElement = document.createElement('link')
        linkElement.rel = 'icon'
        document.getElementsByTagName('head')[0].appendChild(linkElement)
      }
      
      linkElement.href = fullLogoUrl
      
      if (logoPath.endsWith('.svg')) {
        linkElement.type = 'image/svg+xml'
      } else if (logoPath.endsWith('.png')) {
        linkElement.type = 'image/png'
      } else {
        linkElement.type = 'image/x-icon'
      }
    }
  }, [settings])

  useEffect(() => {
    client.get('/ads?ubicacion=landing')
      .then(res => {
        setAds(res.data?.datos || [])
      })
      .catch(err => {
        console.error('Error fetching ads:', err)
      })
  }, [])

  useEffect(() => {
    // 1. Detectar si ya está instalada o se ejecuta en standalone
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    
    if (isStandalone) {
      setIsInstalled(true);
    }

    // 2. Comprobar si el evento ya fue capturado globalmente
    if (window.deferredPrompt) {
      setDeferredPrompt(window.deferredPrompt);
      setIsInstallable(true);
    }

    // 3. Escuchar el evento custom y el evento nativo
    const handlePromptAvailable = () => {
      if (window.deferredPrompt) {
        setDeferredPrompt(window.deferredPrompt);
        setIsInstallable(true);
      }
    };

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      window.deferredPrompt = e;
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      window.deferredPrompt = null;
    };

    window.addEventListener('pwa-prompt-available', handlePromptAvailable);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('pwa-prompt-available', handlePromptAvailable);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const isStandaloneGlobal = window.matchMedia('(display-mode: standalone)').matches 
    || window.navigator.standalone 
    || document.referrer.includes('android-app://');

  const handleInstallClick = async () => {
    const promptEvent = deferredPrompt || window.deferredPrompt;
    if (!promptEvent) {
      const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      if (isiOS) {
        setShowiOSModal(true);
      } else {
        setShowAndroidModal(true);
      }
      return;
    }
    
    promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
      setIsInstallable(false);
      window.deferredPrompt = null;
      setDeferredPrompt(null);
    }
  };

  const showInstallButton = !isInstalled && !isStandaloneGlobal;

  const nombreSistema = settings?.nombre_sistema || 'SubiGol'
  const logoSistema = settings?.logo_sistema || '/favicon.svg'
  const baseImgUrl = (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.startsWith('http'))
    ? import.meta.env.VITE_API_URL.replace('/api', '')
    : (window.__APP_BASE_URL__ || '/').replace(/\/$/, '')
  const logoUrl = logoSistema.startsWith('/uploads') ? `${baseImgUrl}${logoSistema}` : (logoSistema.startsWith('/') ? `${window.__APP_BASE_URL__ || '/'}${logoSistema.substring(1)}` : logoSistema)

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0D1B2A' }}>
      {/* ============================================
          NAVBAR SIMPLE PARA LANDING
          ============================================ */}
      <nav
        className="fixed top-0 left-0 right-0 z-50"
        style={{
          background: 'rgba(13, 27, 42, 0.8)',
          backdropFilter: 'blur(16px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
              <img
                src={logoUrl}
                alt="Logo"
                className="w-9 h-9 object-contain transition-transform duration-700 ease-out group-hover:rotate-[360deg] group-hover:scale-110"
              />
              <span className="font-display font-bold text-lg" style={{ color: '#F1F5F9' }}>
                {nombreSistema}
              </span>
            </Link>

            {/* Enlaces de escritorio */}
            <div className="hidden md:flex items-center gap-6">
              <Link
                to="/"
                className="text-sm font-display font-medium transition-colors hover:text-cyan"
                style={{ color: '#00C2FF' }}
              >
                Inicio
              </Link>
              <Link
                to="/partidos"
                className="text-sm font-display font-medium transition-colors hover:text-cyan"
                style={{ color: '#94A3B8' }}
              >
                Partidos
              </Link>
              <Link
                to="/clasificacion"
                className="text-sm font-display font-medium transition-colors hover:text-cyan"
                style={{ color: '#94A3B8' }}
              >
                Clasificación
              </Link>
            </div>

            {/* Acciones de escritorio */}
            <div className="hidden md:flex items-center gap-3">
              {showInstallButton && (
                <button
                  onClick={handleInstallClick}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-display font-semibold transition-all hover:scale-105 border"
                  style={{
                    background: 'rgba(0, 194, 255, 0.1)',
                    color: '#00C2FF',
                    borderColor: 'rgba(0, 194, 255, 0.3)',
                    boxShadow: '0 0 15px rgba(0, 194, 255, 0.1)',
                  }}
                >
                  <Download className="w-4 h-4 shrink-0 animate-pulse" />
                  Instalar App
                </button>
              )}
              <Link to="/login">
                <button
                  className="px-4 py-2 rounded-lg text-sm font-display font-medium transition-colors hover:opacity-80"
                  style={{ color: '#94A3B8' }}
                >
                  Iniciar sesión
                </button>
              </Link>
              <Link to="/registro">
                <button
                  className="px-5 py-2 rounded-lg text-sm font-display font-semibold transition-all hover:scale-105"
                  style={{
                    background: '#00C2FF',
                    color: '#061530',
                    boxShadow: '0 0 20px rgba(0, 194, 255, 0.15)',
                  }}
                >
                  Registrarse
                </button>
              </Link>
            </div>

            {/* Botón de instalación en móvil (Navbar) */}
            {showInstallButton && (
              <button
                onClick={handleInstallClick}
                className="md:hidden flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-cyan/15 border border-cyan/30 text-cyan text-[10px] font-bold uppercase tracking-wider hover:bg-cyan/25 transition-all mr-1"
                style={{
                  backgroundColor: 'rgba(0, 194, 255, 0.15)',
                  color: '#00C2FF',
                  borderColor: 'rgba(0, 194, 255, 0.3)',
                }}
                title="Instalar aplicación"
              >
                <Download className="w-3.5 h-3.5 shrink-0" />
                <span>Instalar</span>
              </button>
            )}

            {/* Hamburguesa móvil */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg transition-colors"
              style={{ color: '#94A3B8' }}
              aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Menú móvil desplegable */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden overflow-hidden"
              style={{
                background: 'rgba(13, 27, 42, 0.95)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
              }}
            >
              <div className="px-4 py-4 space-y-3 flex flex-col">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-sm font-display font-medium py-1.5 transition-colors"
                  style={{ color: '#00C2FF' }}
                >
                  Inicio
                </Link>
                <Link
                  to="/partidos"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-sm font-display font-medium py-1.5 transition-colors hover:text-cyan"
                  style={{ color: '#94A3B8' }}
                >
                  Partidos
                </Link>
                <Link
                  to="/clasificacion"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-sm font-display font-medium py-1.5 transition-colors hover:text-cyan"
                  style={{ color: '#94A3B8' }}
                >
                  Clasificación
                </Link>
                <div className="border-t border-white/10 pt-3 flex flex-col gap-2.5">
                {showInstallButton && (
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleInstallClick();
                    }}
                    className="w-full py-2.5 rounded-lg text-sm font-display font-semibold transition-all flex items-center justify-center gap-2 border"
                    style={{
                      background: 'rgba(0, 194, 255, 0.1)',
                      color: '#00C2FF',
                      borderColor: 'rgba(0, 194, 255, 0.3)',
                    }}
                  >
                    <Download className="w-4 h-4" />
                    Instalar Aplicación
                  </button>
                )}
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="w-full">
                  <button
                    className="w-full py-2.5 rounded-lg text-sm font-display font-medium transition-colors bg-white/5 border border-white/10 hover:bg-white/10"
                    style={{ color: '#F1F5F9' }}
                  >
                    Iniciar sesión
                  </button>
                </Link>
                  <Link to="/registro" onClick={() => setMobileMenuOpen(false)} className="w-full">
                    <button
                      className="w-full py-2.5 rounded-lg text-sm font-display font-semibold transition-all"
                      style={{
                        background: '#00C2FF',
                        color: '#061530',
                      }}
                    >
                      Registrarse
                    </button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ============================================
          HERO SECTION
          ============================================ */}
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16"
        style={{
          background: 'radial-gradient(ellipse at top, rgba(0, 194, 255, 0.12) 0%, transparent 60%), linear-gradient(180deg, #0A1F44 0%, #0D1B2A 100%)',
        }}
      >
        {/* Patrón de fondo sutil */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />

        {/* Partículas decorativas animadas */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: '#00C2FF', opacity: 0.3 }}
              animate={{
                y: [0, -120, 0],
                x: [0, Math.random() * 50 - 25, 0],
                opacity: [0, 0.5, 0],
              }}
              transition={{
                duration: 5 + Math.random() * 3,
                repeat: Infinity,
                delay: i * 1,
                ease: 'easeInOut',
              }}
              initial={{
                left: `${15 + i * 13}%`,
                top: `${65 + Math.random() * 25}%`,
              }}
            />
          ))}
        </div>

        {/* Contenido del Hero */}
        <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 text-center flex justify-center">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="visible"
            className="flex flex-col items-center w-full"
          >
            {/* Etiqueta superior */}
            <motion.div variants={fadeUp}>
              <span
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium mb-8"
                style={{
                  background: 'rgba(0, 194, 255, 0.08)',
                  border: '1px solid rgba(0, 194, 255, 0.25)',
                  color: '#00C2FF',
                }}
              >
                <Globe className="w-3.5 h-3.5" />
                Mundial 2026 · Estados Unidos, México y Canadá
              </span>
            </motion.div>

            {/* Título principal */}
            <motion.h1
              variants={fadeUp}
              className="w-full font-display font-black text-4xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.05] mb-6 px-2"
              style={{ color: '#F1F5F9' }}
            >
              Predice.{' '}
              <span
                style={{
                  background: 'linear-gradient(135deg, #00C2FF 0%, #66D9FF 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Compite.
              </span>
              <br />
              Demuestra quién sabe.
            </motion.h1>

            {/* Subtítulo */}
            <motion.p
              variants={fadeUp}
              className="w-full text-base sm:text-lg md:text-xl max-w-2xl mb-10 leading-relaxed px-4 mx-auto"
              style={{ color: '#94A3B8' }}
            >
              Arma tu bracket completo del Mundial 2026, crea comunidades con tus amigos
              y sube en la clasificación con cada predicción acertada.
            </motion.p>

            {/* Cuenta regresiva */}
            <motion.div variants={fadeUp} className="w-full mb-10 flex flex-col items-center justify-center px-4">
              <Countdown matches={matches} />
            </motion.div>

            {/* CTAs */}
            <motion.div variants={fadeUp} className="w-full flex flex-col sm:flex-row gap-3 justify-center items-center px-4">
              <Link to="/registro" className="w-full sm:w-auto">
                <button
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl text-lg font-display font-semibold transition-all hover:scale-[1.03] active:scale-[0.98]"
                  style={{
                    background: '#00C2FF',
                    color: '#061530',
                    boxShadow: '0 0 30px rgba(0, 194, 255, 0.2)',
                  }}
                >
                  Crear mi predicción
                  <ArrowRight className="w-5 h-5" />
                </button>
              </Link>
              <Link to="/login" className="w-full sm:w-auto">
                <button
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-lg font-display font-semibold transition-all hover:scale-[1.03]"
                  style={{
                    background: 'transparent',
                    color: '#00C2FF',
                    border: '2px solid #00C2FF',
                  }}
                >
                  Ya tengo cuenta
                </button>
              </Link>
            </motion.div>

            {/* Prueba social */}
            <motion.p variants={fadeUp} className="w-full mt-8 text-sm flex flex-wrap items-center justify-center gap-2.5 px-4" style={{ color: '#64748B' }}>
              <span className="flex -space-x-2">
                {['#00C2FF', '#22C55E', '#F59E0B', '#EF4444'].map((bg, i) => (
                  <span
                    key={i}
                    className="w-7 h-7 rounded-full flex items-center justify-center text-[0.55rem] font-bold text-white"
                    style={{ backgroundColor: bg, border: '2px solid #0D1B2A' }}
                  >
                    {String.fromCharCode(65 + i)}
                  </span>
                ))}
              </span>
              Únete a miles de fanáticos que ya armaron su bracket
            </motion.p>
          </motion.div>
        </div>

        {/* Indicador de scroll */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div
            className="w-5 h-8 rounded-full flex justify-center pt-1.5"
            style={{ border: '2px solid rgba(100, 116, 139, 0.3)' }}
          >
            <div className="w-1 h-2 rounded-full" style={{ background: 'rgba(100, 116, 139, 0.5)' }} />
          </div>
        </motion.div>
      </section>

      {/* ============================================
          CARACTERÍSTICAS
          ============================================ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 w-full flex justify-center">
        <div className="w-full max-w-6xl mx-auto">
          {/* Carrusel de Auspiciadores */}
          {ads.length > 0 && <AdCarousel ads={ads} variant="large" />}

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="text-center mb-16 w-full px-4"
          >
            <motion.h2
              variants={fadeUp}
              className="w-full font-display font-bold text-3xl sm:text-4xl lg:text-5xl mb-4"
              style={{ color: '#F1F5F9' }}
            >
              Todo lo que necesitas para competir
            </motion.h2>
            <motion.p variants={fadeUp} className="w-full max-w-xl mx-auto text-base" style={{ color: '#94A3B8' }}>
              SubiGol no es solo un formulario de predicciones. Es la experiencia completa del Mundial.
            </motion.p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 px-4"
          >
            <Feature
              icon={Trophy}
              title="Bracket interactivo"
              description="Predice cada fase: grupos, octavos, cuartos, semis y la gran final. Tu bracket cobra vida con cada resultado real."
            />
            <Feature
              icon={Users}
              title="Comunidades privadas"
              description="Crea comunidades con tus amigos, familia o compañeros de trabajo. Comparte un código y compitan entre ustedes."
            />
            <Feature
              icon={BarChart3}
              title="Clasificación en vivo"
              description="Sigue tu posición en tiempo real. Los puntos se actualizan automáticamente con cada resultado del Mundial."
            />
            <Feature
              icon={Zap}
              title="Puntuación inteligente"
              description="Gana puntos por resultado exacto, resultado correcto, clasificados acertados y premios individuales."
            />
            <Feature
              icon={Shield}
              title="Predicciones seguras"
              description="Tus predicciones se bloquean antes del inicio de cada fase. Nadie puede cambiarlas después."
            />
            <Feature
              icon={Star}
              title="Premios individuales"
              description="Predice el máximo goleador y mejor jugador del torneo para obtener puntos extra."
            />
          </motion.div>
        </div>
      </section>

      {/* ============================================
          CÓMO FUNCIONA
          ============================================ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 w-full flex justify-center" style={{ background: 'rgba(10, 31, 68, 0.3)' }}>
        <div className="w-full max-w-3xl mx-auto">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="w-full"
          >
            <motion.div variants={fadeUp} className="text-center mb-16 w-full px-4">
              <h2 className="w-full font-display font-bold text-3xl sm:text-4xl lg:text-5xl mb-4" style={{ color: '#F1F5F9' }}>
                Empieza en 3 pasos
              </h2>
              <p className="w-full text-base" style={{ color: '#94A3B8' }}>
                No necesitas ser experto. Solo necesitas pasión por el fútbol.
              </p>
            </motion.div>

            <div className="space-y-10 w-full px-4">
              <Step
                number="1"
                title="Crea tu cuenta"
                description="Regístrate en menos de un minuto. Solo necesitas un correo y una contraseña."
              />
              <Step
                number="2"
                title="Arma tu predicción"
                description="Usa el asistente de predicciones para definir posiciones de grupo, bracket eliminatorio y premios individuales."
              />
              <Step
                number="3"
                title="Compite y sube"
                description="Únete a comunidades, sigue la clasificación en vivo y celebra cada predicción acertada."
              />
            </div>

            <motion.div variants={fadeUp} className="mt-14 text-center w-full px-4">
              <Link to="/registro" className="w-full sm:w-auto inline-block">
                <button
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-base font-display font-semibold transition-all hover:scale-105"
                  style={{
                    background: '#00C2FF',
                    color: '#061530',
                    boxShadow: '0 0 20px rgba(0, 194, 255, 0.15)',
                  }}
                >
                  Comenzar ahora
                  <ChevronRight className="w-5 h-5" />
                </button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ============================================
          PUNTUACIÓN
          ============================================ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 w-full flex justify-center">
        <div className="w-full max-w-3xl mx-auto">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="w-full"
          >
            <motion.div variants={fadeUp} className="text-center mb-14 w-full px-4">
              <h2 className="w-full font-display font-bold text-3xl sm:text-4xl lg:text-5xl mb-4" style={{ color: '#F1F5F9' }}>
                Sistema de puntuación
              </h2>
              <p className="w-full max-w-xl mx-auto text-base" style={{ color: '#94A3B8' }}>
                Cada predicción correcta suma puntos. Mientras más difícil, más valor.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} className="w-full px-4">
              <div
                className="w-full rounded-2xl overflow-hidden"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(16px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  overflowX: 'auto',
                }}
              >
                <table className="w-full text-sm min-w-[320px]">
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                      <th className="text-left px-6 py-4 font-display font-semibold" style={{ color: '#F1F5F9' }}>
                        Predicción
                      </th>
                      <th className="text-right px-6 py-4 font-display font-semibold" style={{ color: '#00C2FF' }}>
                        Puntos
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {scoringRules.map(([prediction, points], i) => (
                      <tr
                        key={i}
                        className="transition-colors"
                        style={{
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <td className="px-6 py-3.5" style={{ color: '#94A3B8' }}>{prediction}</td>
                        <td className="px-6 py-3.5 text-right font-mono font-bold" style={{ color: '#00C2FF' }}>
                          {points}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ============================================
          CTA FINAL
          ============================================ */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 w-full flex justify-center">
        <div className="w-full max-w-3xl mx-auto text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="w-full rounded-3xl p-6 sm:p-14"
            style={{
              background: 'rgba(0, 194, 255, 0.05)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(0, 194, 255, 0.3)',
              boxShadow: '0 0 40px rgba(0, 194, 255, 0.12)',
            }}
          >
            <Trophy className="w-14 h-14 mx-auto mb-5" style={{ color: '#F59E0B' }} />
            <h2 className="w-full font-display font-bold text-2xl sm:text-4xl mb-4" style={{ color: '#F1F5F9' }}>
              ¿Listo para demostrar lo que sabes?
            </h2>
            <p className="w-full mb-8 max-w-lg mx-auto text-sm sm:text-base leading-relaxed" style={{ color: '#94A3B8' }}>
              El Mundial 2026 será el más grande de la historia. No te quedes afuera.
              Arma tu predicción y compite con el mundo.
            </p>
            <Link to="/registro" className="w-full sm:w-auto inline-block">
              <button
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl text-lg font-display font-semibold transition-all hover:scale-105"
                style={{
                  background: '#00C2FF',
                  color: '#061530',
                  boxShadow: '0 0 30px rgba(0, 194, 255, 0.2)',
                }}
              >
                Crear cuenta gratis
                <ArrowRight className="w-5 h-5" />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer completo */}
      <Footer />

      {/* Modales de instrucciones de instalación */}
      {showiOSModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 font-body animate-fade-in text-center">
          <div className="bg-navy border border-border-subtle rounded-2xl w-full max-w-sm p-6 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-border-subtle pb-3">
              <h3 className="font-display font-bold text-base text-text-primary text-left" style={{ color: '#F1F5F9' }}>Instalar Aplicación en iOS</h3>
              <button onClick={() => setShowiOSModal(false)} className="text-text-muted hover:text-text-primary" style={{ color: '#94A3B8' }}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3.5 text-left text-sm text-text-secondary" style={{ color: '#94A3B8' }}>
              <p>Sigue estos sencillos pasos para instalar <strong className="text-text-primary font-semibold" style={{ color: '#F1F5F9' }}>{nombreSistema}</strong> en tu iPhone o iPad:</p>
              <ol className="list-decimal list-inside space-y-2.5 bg-navy-dark/40 border border-border-subtle p-3.5 rounded-xl text-xs" style={{ borderColor: 'rgba(255, 255, 255, 0.08)', backgroundColor: 'rgba(13, 27, 42, 0.4)' }}>
                <li>Presiona el botón de <strong>Compartir</strong> en la barra inferior de Safari (el icono de un cuadro con una flecha apuntando hacia arriba <span className="inline-block p-1 bg-white/10 rounded">📤</span>).</li>
                <li>Desplázate hacia abajo en el menú y selecciona la opción <strong>"Agregar a inicio"</strong> o <strong>"A la pantalla de inicio"</strong>.</li>
                <li>Presiona <strong>"Agregar"</strong> en la esquina superior derecha para confirmar la instalación.</li>
              </ol>
            </div>
            <button
              onClick={() => setShowiOSModal(false)}
              className="w-full bg-cyan text-navy-dark font-display font-bold text-sm py-2 rounded-xl hover:bg-cyan-light transition-colors"
              style={{ backgroundColor: '#00C2FF', color: '#061530' }}
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      {showAndroidModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 font-body animate-fade-in text-center">
          <div className="bg-navy border border-border-subtle rounded-2xl w-full max-w-sm p-6 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-border-subtle pb-3">
              <h3 className="font-display font-bold text-base text-text-primary text-left" style={{ color: '#F1F5F9' }}>Instalar Aplicación</h3>
              <button onClick={() => setShowAndroidModal(false)} className="text-text-muted hover:text-text-primary" style={{ color: '#94A3B8' }}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3.5 text-left text-sm text-text-secondary" style={{ color: '#94A3B8' }}>
              <p>Sigue estos pasos para instalar <strong className="text-text-primary font-semibold" style={{ color: '#F1F5F9' }}>{nombreSistema}</strong>:</p>
              <ol className="list-decimal list-inside space-y-2.5 bg-navy-dark/40 border border-border-subtle p-3.5 rounded-xl text-xs" style={{ borderColor: 'rgba(255, 255, 255, 0.08)', backgroundColor: 'rgba(13, 27, 42, 0.4)' }}>
                <li>Presiona el botón de menú de tu navegador (los tres puntos en la esquina superior derecha).</li>
                <li>Selecciona la opción <strong>"Instalar aplicación"</strong> o <strong>"Agregar a la pantalla principal"</strong>.</li>
                <li>Confirma la acción para completar la instalación.</li>
              </ol>
            </div>
            <button
              onClick={() => setShowAndroidModal(false)}
              className="w-full bg-cyan text-navy-dark font-display font-bold text-sm py-2 rounded-xl hover:bg-cyan-light transition-colors"
              style={{ backgroundColor: '#00C2FF', color: '#061530' }}
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
